<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';

const CHANNELS = [
  { value: '', label: 'Todos os canais' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'site', label: 'Site' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'E-mail' },
  { value: 'manual', label: 'Manual' },
];

const STATUSES = [
  { value: '', label: 'Todos os status' },
  { value: 'novo', label: 'Novo' },
  { value: 'em_atendimento_ia', label: 'IA atendendo' },
  { value: 'coletando_dados', label: 'Coletando dados' },
  { value: 'qualificando', label: 'Qualificando' },
  { value: 'lead_frio', label: 'Lead frio' },
  { value: 'lead_morno', label: 'Lead morno' },
  { value: 'lead_quente', label: 'Lead quente' },
  { value: 'pronto_para_agendamento', label: 'Pronto para agendar' },
  { value: 'aguardando_atendente', label: 'Aguardando atendente' },
  { value: 'em_atendimento_humano', label: 'Atendimento humano' },
  { value: 'agendado', label: 'Agendado' },
  { value: 'sem_resposta', label: 'Sem resposta' },
  { value: 'perdido', label: 'Perdido' },
];

const PRIORITIES = [
  { value: '', label: 'Todas prioridades' },
  { value: 'alta', label: 'Alta' },
  { value: 'media', label: 'Media' },
  { value: 'baixa', label: 'Baixa' },
];

const columns = ref([]);
const totals = ref({ leads: 0 });
const isLoading = ref(true);
const errorMessage = ref('');
const filters = ref({ channel: '', status: '', priority: '' });
const router = useRouter();
const { accountId } = useAccount();

const hasLeads = computed(() =>
  columns.value.some(column => column.leads && column.leads.length > 0)
);

function formatDate(value) {
  if (!value) return 'Sem data';
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function channelLabel(channel) {
  const found = CHANNELS.find(item => item.value === channel);
  return found?.label || channel || 'Manual';
}

function statusLabel(status) {
  const found = STATUSES.find(item => item.value === status);
  return found?.label || status || 'Sem status';
}

function priorityClass(priority) {
  const classes = {
    alta: 'bg-n-ruby-3 text-n-ruby-11',
    media: 'bg-n-amber-3 text-n-amber-11',
    baixa: 'bg-n-slate-3 text-n-slate-11',
  };
  return classes[priority] || 'bg-n-slate-3 text-n-slate-11';
}

function appointmentLabel(appointment) {
  if (!appointment) return '';
  const date = new Date(`${appointment.scheduled_date}T00:00:00`);
  const formattedDate = date.toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  });
  return `${formattedDate}, ${appointment.scheduled_time.slice(0, 5)}`;
}

function leadPreview(lead) {
  return lead.lastMessage || lead.summary || lead.nextBestAction || 'Sem historico recente.';
}

function openConversation(lead) {
  if (!lead.conversationId) return;
  router.push(`/app/accounts/${accountId.value}/conversations/${lead.conversationId}`);
}

async function fetchPipeline() {
  isLoading.value = true;
  errorMessage.value = '';
  const params = new URLSearchParams();
  Object.entries(filters.value).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });

  try {
    const query = params.toString();
    const response = await fetch(`/api/superwoot/pipeline${query ? `?${query}` : ''}`);
    if (!response.ok) throw new Error('request_failed');
    const payload = await response.json();
    columns.value = payload.columns || [];
    totals.value = payload.totals || { leads: 0 };
  } catch {
    errorMessage.value = 'Nao foi possivel carregar o pipeline.';
  } finally {
    isLoading.value = false;
  }
}

watch(filters, fetchPipeline, { deep: true });
onMounted(fetchPipeline);
</script>

<template>
  <section class="flex flex-col flex-1 min-w-0 h-full bg-n-background">
    <header
      class="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-n-weak bg-n-solid-2"
    >
      <div>
        <h1 class="text-xl font-medium text-n-slate-12">Pipeline</h1>
        <p class="mt-1 text-sm text-n-slate-11">
          Leads por etapa da conversa omnichannel
        </p>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <select
          v-model="filters.channel"
          class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12"
        >
          <option
            v-for="channel in CHANNELS"
            :key="channel.value"
            :value="channel.value"
          >
            {{ channel.label }}
          </option>
        </select>
        <select
          v-model="filters.status"
          class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12"
        >
          <option
            v-for="status in STATUSES"
            :key="status.value"
            :value="status.value"
          >
            {{ status.label }}
          </option>
        </select>
        <select
          v-model="filters.priority"
          class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12"
        >
          <option
            v-for="priority in PRIORITIES"
            :key="priority.value"
            :value="priority.value"
          >
            {{ priority.label }}
          </option>
        </select>
        <Button
          icon="i-lucide-refresh-cw"
          color="slate"
          size="sm"
          title="Atualizar pipeline"
          @click="fetchPipeline"
        />
      </div>
    </header>

    <div
      v-if="errorMessage"
      class="px-6 py-4 text-sm text-n-ruby-11"
    >
      {{ errorMessage }}
    </div>

    <div class="flex items-center gap-3 px-6 py-3 border-b border-n-weak bg-n-solid-1">
      <span class="text-sm text-n-slate-11">
        {{ totals.leads || 0 }} leads
      </span>
      <span
        v-if="isLoading"
        class="text-sm text-n-slate-10"
      >
        Atualizando...
      </span>
    </div>

    <div class="flex-1 min-h-0 p-4 overflow-auto">
      <div
        class="grid min-w-[1560px] h-full gap-3"
        style="grid-template-columns: repeat(10, minmax(148px, 1fr));"
      >
        <section
          v-for="column in columns"
          :key="column.id"
          class="flex flex-col min-w-0 overflow-hidden border rounded-lg border-n-weak bg-n-solid-2"
        >
          <header
            class="flex items-center justify-between px-3 py-3 border-b border-n-weak bg-n-solid-3"
          >
            <h2 class="text-sm font-medium truncate text-n-slate-12">
              {{ column.title }}
            </h2>
            <span class="text-xs text-n-slate-10">
              {{ column.count }}
            </span>
          </header>

          <div class="flex-1 p-2 overflow-y-auto">
            <p
              v-if="!isLoading && column.leads.length === 0"
              class="px-1 py-2 text-xs text-n-slate-10"
            >
              Sem leads
            </p>

            <button
              v-for="lead in column.leads"
              :key="lead.id"
              type="button"
              class="w-full p-2 mb-2 text-left border rounded-md border-n-weak bg-n-solid-3 hover:bg-n-alpha-2 disabled:cursor-default disabled:opacity-70"
              :disabled="!lead.conversationId"
              @click="openConversation(lead)"
            >
              <div class="flex items-start justify-between gap-2">
                <strong class="min-w-0 text-sm font-medium truncate text-n-slate-12">
                  {{ lead.name }}
                </strong>
                <span
                  class="shrink-0 px-1.5 py-0.5 text-xs rounded"
                  :class="priorityClass(lead.priority)"
                >
                  {{ lead.score }}
                </span>
              </div>

              <div class="flex flex-wrap gap-1 mt-2">
                <span class="px-1.5 py-0.5 text-xs rounded bg-n-blue-3 text-n-blue-11">
                  {{ channelLabel(lead.channel) }}
                </span>
                <span class="px-1.5 py-0.5 text-xs rounded bg-n-slate-3 text-n-slate-11">
                  {{ statusLabel(lead.status) }}
                </span>
              </div>

              <p class="mt-2 text-xs leading-5 line-clamp-3 text-n-slate-11">
                {{ leadPreview(lead) }}
              </p>

              <p
                v-if="lead.appointment"
                class="mt-2 text-xs font-medium text-n-teal-11"
              >
                {{ appointmentLabel(lead.appointment) }}
              </p>

              <time class="block mt-2 text-xs text-n-slate-10">
                {{ formatDate(lead.lastInteractionAt) }}
              </time>
            </button>
          </div>
        </section>
      </div>

      <p
        v-if="!isLoading && !hasLeads"
        class="mt-4 text-sm text-n-slate-11"
      >
        Nenhum lead encontrado para os filtros atuais.
      </p>
    </div>
  </section>
</template>
